import { ReactNode } from "react";
import { cn } from "@shared/utils/cn";

interface BookingChromeProps {
  children: ReactNode;
  /**
   * Card width cap.
   * - 'wide' (default) — `max-w-2xl` (672px). The booking flow and most
   *   standalone pages (Profile, Review, confirmations, gift cards, etc.)
   * - 'narrow' — `max-w-md` (448px). Used by tighter content pages like
   *   ExperiencesPage where the wider card feels too loose.
   */
  width?: "narrow" | "wide";
  /**
   * Apply `flex flex-col` to the inner card so children flow as columns.
   * Pages with stacked sections (header + content + footer) pass `true`.
   */
  flexCol?: boolean;
  /** Additional classes appended to the inner card. */
  cardClassName?: string;
}

/**
 * Standard mobile-first chrome for the booking widget.
 *
 * - Below `lg` (1024px): full-bleed — no shadow, no top corners, no outer
 *   padding. Bottom corners stay rounded for a soft visual close.
 * - At `lg+`: floating card with shadow, all corners rounded, outer padding.
 *
 * Auth pages (AuthLoginPage, AuthSignupPage, ForgotPasswordPage,
 * ResetPasswordPage) have a different chrome — they vertically center the
 * card and render a "Powered by Meitre" sibling outside it — and don't use
 * this component.
 */
export function BookingChrome({
  children,
  width = "wide",
  flexCol = false,
  cardClassName,
}: BookingChromeProps) {
  return (
    <div className="min-h-screen flex flex-col items-center p-0 lg:p-4">
      <div
        className={cn(
          "relative z-10 animate-fade-in w-full bg-card",
          "shadow-none rounded-b-2xl lg:shadow-lg lg:rounded-2xl",
          "overflow-visible lg:overflow-hidden",
          width === "narrow" ? "max-w-md" : "max-w-2xl",
          flexCol && "flex flex-col",
          cardClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}
